import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { projects } from "../data/projectsData";
import Button from "./Button";
import SectionTitle from "./SectionTitle";
import { Github, ExternalLink, Sparkles, CheckCircle2, X, Info } from "lucide-react";

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = ["All", "Mobile Apps", "SaaS & Web Apps", "Admin & Enterprise", "E-Commerce"];

  const filteredProjects = activeCategory === "All"
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  return (
    <section id="projects" className="py-24 bg-gray-50/30 dark:bg-gray-950/20 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <SectionTitle
          title="Featured Projects"
          subtitle="Explore key mobile applications, full-stack web platforms, and SaaS products"
        />

        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-8 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer ${
                activeCategory === cat
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/20 scale-105"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredProjects.map((project) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                key={project.id}
                className="group bg-white dark:bg-gray-900 rounded-3xl overflow-hidden border border-gray-200/80 dark:border-gray-800 shadow-lg hover:shadow-2xl hover:shadow-indigo-500/10 dark:hover:shadow-black/50 transform hover:-translate-y-2 transition-all duration-500 flex flex-col justify-between relative"
              >
                {/* Flagship SaaS Glow Badge */}
                {project.featured && (
                  <div className="absolute top-4 left-4 z-20 px-3.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-white text-[11px] font-extrabold uppercase tracking-wider flex items-center shadow-lg gap-1 animate-pulse-glow">
                    <Sparkles className="w-3 h-3" />
                    <span>Flagship SaaS</span>
                  </div>
                )}

                <div>
                  {/* Image Container with Screenshot Hover Zoom */}
                  <div className="relative h-56 overflow-hidden bg-gray-100 dark:bg-gray-800 border-b border-gray-200/60 dark:border-gray-800">
                    {project.imageUrl ? (
                      <img
                        src={project.imageUrl}
                        alt={project.title}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                        <span className="text-white text-lg font-bold font-heading">{project.title}</span>
                      </div>
                    )}
                    
                    {/* Dark gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      {project.metric && (
                        <span className="text-xs font-bold text-white bg-indigo-600/90 backdrop-blur-md px-3 py-1 rounded-full">
                          ⚡ {project.metric}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl font-bold font-heading text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                        {project.title}
                      </h3>
                    </div>

                    {project.subtitle && (
                      <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-3">
                        {project.subtitle}
                      </p>
                    )}

                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-5 line-clamp-3">
                      {project.description}
                    </p>

                    {/* Tech Stack Badges */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {project.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 rounded-lg font-semibold border border-indigo-100 dark:border-indigo-900/40"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer Action Buttons */}
                <div className="p-6 pt-0 flex items-center space-x-3 mt-auto">
                  <Button
                    variant="primary"
                    href={project.projectUrl}
                    className="flex-1 py-2.5 text-xs sm:text-sm font-semibold shadow-md"
                  >
                    <span>Live Demo</span>
                    <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                  </Button>

                  {project.highlights && (
                    <button
                      onClick={() => setSelectedProject(project)}
                      className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors cursor-pointer"
                      title="View Details & Highlights"
                    >
                      <Info className="w-5 h-5" />
                    </button>
                  )}

                  {project.githubUrl && (
                    <Button
                      variant="outline"
                      href={project.githubUrl}
                      className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500"
                    >
                      <Github className="w-5 h-5 text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* View All Projects Button */}
        <div className="text-center mt-16">
          <Button
            variant="secondary"
            href="https://github.com/matin808"
            className="px-8 shadow-sm hover:shadow-md py-3 text-sm font-semibold"
          >
            <Github className="w-4 h-4 mr-2" />
            Explore More Repositories on GitHub
          </Button>
        </div>
      </div>

      {/* Project Highlight Modal */}
      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-xl w-full p-6 sm:p-8 relative border border-gray-200 dark:border-gray-800 shadow-2xl overflow-y-auto max-h-[90vh]"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-5 right-5 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-2">
                <span>{selectedProject.category}</span>
                {selectedProject.metric && (
                  <span className="bg-indigo-50 dark:bg-indigo-950 px-2 py-0.5 rounded-full text-indigo-700 dark:text-indigo-300">
                    ⚡ {selectedProject.metric}
                  </span>
                )}
              </div>

              <h3 className="text-2xl font-bold font-heading text-gray-900 dark:text-white mb-2">
                {selectedProject.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-6 leading-relaxed">
                {selectedProject.description}
              </p>

              <div className="mb-6">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Key Technical Achievements:
                </h4>
                <ul className="space-y-2.5">
                  {selectedProject.highlights.map((h, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2.5 shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex space-x-3 pt-2 border-t border-gray-100 dark:border-gray-800">
                <Button
                  variant="primary"
                  href={selectedProject.projectUrl}
                  className="flex-1 py-3 text-sm"
                >
                  <span>Launch Project Live</span>
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
                {selectedProject.githubUrl && (
                  <Button
                    variant="outline"
                    href={selectedProject.githubUrl}
                    className="py-3 px-4 text-sm"
                  >
                    <Github className="w-4 h-4 mr-2" />
                    <span>Source</span>
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;